"use client";

import { useSearchParams } from "next/navigation";
import {
  FinancialWorkDocThumb,
  type DocThumbGlyph,
} from "@/app/financial-work/FinancialWorkDocThumb";
import {
  formatThaiDatetime,
  formatThaiDatetimePartsFromDate,
  getDisplayText,
  parseDatetimeInput,
} from "@aera/data-table";
import { getUserIdFromAuthToken } from "@/lib/getUserIdFromAuthToken";
import { resolveQueueCardImageUrl } from "@/lib/financialWork/queueCardThumb";
import {
  batchSetLabel,
  cardBatchClosed,
  cardBatchSize,
  displayWorkTitle,
  isReceivableBatchCard,
  isSharedBatchCard,
  isSlipOwnerAskCard,
  askKindFromCard,
  workStatusLabel,
} from "@/lib/financialWork/nav";
import { queueRowShowsClosedStyle } from "@/lib/financialWork/feedClient";
import {
  closedProvenanceSourceLabel,
  describeClosedQueueRow,
  type ClosedQueueRowDisplay,
} from "@/lib/financialWork/incomeRecordQueueDisplay";
import type { LiffFinancialWorkCard } from "@/lib/financialWork/types";

export type WorkQueueRowModel = {
  card: LiffFinancialWorkCard;
  closedView: boolean;
  fromIncome: boolean;
  grouped: boolean;
  occurredAt: string | null;
  title: string;
  subtitle: string | null;
  matchLabel: string | null;
  sourceLabel: string | null;
  related: string;
  relatedNote: string | null;
  moneyIn: string | null;
  moneyOut: string | null;
  status: { title: string; detail?: string; tone: WorkQueueRowStatusTone };
  moneyStatus: string | null;
  incomeCard: boolean;
  waitingMatch: boolean;
  unpaid: boolean;
  haystack: string;
};

export type QueueFilterId = "all" | "needs_action" | "waiting" | "recently_closed";

export type WorkQueueRowStatusTone = "danger" | "warn" | "success";

export function workQueueCardKey(card: LiffFinancialWorkCard): string {
  const kind = getDisplayText(card.objectRef?.kind).trim() || "unknown";
  const id = getDisplayText(card.objectRef?.id).trim();
  return id ? `${kind}:${id}` : `${kind}:missing`;
}

function bahtFromMajor(amount: number): string {
  const rounded = Math.round(amount);
  const abs = Math.abs(rounded).toLocaleString("th-TH");
  return rounded < 0 ? `−฿${abs}` : `฿${abs}`;
}

function bahtFromMinor(amountMinor: number): string {
  return bahtFromMajor(amountMinor / 100);
}

function rosterOccurredAt(card: LiffFinancialWorkCard): string | null {
  const child = card.backlogChildren?.find(
    (row) => row.objectRef.id === card.objectRef.id,
  );
  const raw = child?.occurredAt?.trim();
  if (!raw) return null;
  return parseDatetimeInput(raw) ? raw : null;
}

function cardOccurredAt(card: LiffFinancialWorkCard) {
  const candidates = [
    card.contextRows?.find((row) => row.key === "created_at")?.value,
    rosterOccurredAt(card),
    card.timelineSummary?.occurredAt,
    card.timelineEvents?.[0]?.occurredAtLabel,
    card.contextRows?.find((row) => row.key === "occurred_at")?.value,
    card.contextRows?.find((row) => row.key === "updated_at")?.value,
  ];
  for (const raw of candidates) {
    const text = String(raw ?? "").trim();
    if (!text) continue;
    if (parseDatetimeInput(text)) return text;
  }
  return null;
}

function isPartialReceivableCard(card: LiffFinancialWorkCard): boolean {
  if (isSlipOwnerAskCard(card) || card.objectRef.kind !== "receivable") {
    return false;
  }
  const paid = card.outstanding?.items?.[0]?.paidAmountMinor ?? 0;
  const remaining =
    card.outstanding?.remainingAmountMinor ??
    (card.amountSummary?.basis === "remaining"
      ? card.amountSummary.primaryAmountMinor
      : null);
  return paid > 0 && remaining != null && remaining > 0;
}

function retroMatchGrossMinor(card: LiffFinancialWorkCard): number | null {
  if (askKindFromCard(card) !== "retro_match") return null;
  const item = card.outstanding?.items?.[0];
  if (item?.originalAmountMinor != null && item.originalAmountMinor > 0) {
    return item.originalAmountMinor;
  }
  const paid = item?.paidAmountMinor ?? 0;
  const remaining =
    card.outstanding?.remainingAmountMinor ??
    card.amountSummary?.primaryAmountMinor ??
    null;
  if (paid > 0 && remaining != null) return paid + remaining;
  return card.amountSummary?.primaryAmountMinor ?? null;
}

function desktopStatus(card: LiffFinancialWorkCard) {
  const next = card.nextStep?.label ?? "";
  let title = workStatusLabel(card);
  let tone: "danger" | "warn" = "warn";
  if (isSlipOwnerAskCard(card) || title === "ต้องเลือกยอด" || title === "รอการตัดสินใจ") {
    return { title: "รอการตัดสินใจ", detail: next, tone };
  }
  if (isPartialReceivableCard(card)) {
    return { title: "จ่ายบางส่วน รอสลิป", detail: next, tone };
  }
  if (card.surfaceState === "risk" || next.includes("ตรวจบัญชี")) {
    title = "ต้องระวัง";
    tone = "danger";
  } else if (
    title === "รอสลิป" ||
    next.includes("จับคู่") ||
    next.includes("เลือกยอด")
  ) {
    title = "รอจับคู่";
  } else if (card.surfaceState === "needs_review" || title === "ต้องตรวจ") {
    title = "ต้องตรวจ";
  }
  return { title, detail: next, tone };
}

function workRowRailClass(status: {
  title: string;
  tone: WorkQueueRowStatusTone;
}): string {
  if (status.tone === "success") return "";
  const caution = status.tone === "danger" || status.title === "ต้องระวัง";
  return [
    "absolute left-0 top-[15%] h-[70%] w-0.5",
    caution ? "bg-aera-red" : "bg-aera-amber",
  ].join(" ");
}

function WorkQueueStatusMark(props: {
  title: string;
  detail?: string;
  tone: WorkQueueRowStatusTone;
}) {
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <span
        className={`inline-flex rounded-xl px-2 py-0.5 text-[11px] font-bold leading-4 ${
          props.tone === "danger"
            ? "bg-aera-redlight text-aera-red"
            : props.tone === "success"
              ? "bg-aera-greenlight text-aera-green"
              : "bg-aera-warmcream text-aera-amberdark"
        }`}
      >
        {props.title}
      </span>
      {props.detail ? (
        <span className="text-[11px] leading-4 text-aera-gray">{props.detail}</span>
      ) : null}
    </span>
  );
}

function isIncomeCard(card: LiffFinancialWorkCard) {
  const workerId = card.availableActions.find((action) => action.dataNav?.workerId)
    ?.dataNav?.workerId;
  if (
    workerId === "bill-lin-income" ||
    workerId === "bill-lin-receivable" ||
    workerId === "bill-lin-receivable-slip"
  ) {
    return true;
  }
  if (workerId === "bill-lin-expense" || card.objectRef.kind === "expense") {
    return false;
  }
  const haystack = `${card.title} ${card.subtitle ?? ""} ${card.objectRef.kind}`;
  if (/ใบเสร็จ|รายจ่าย|เงินออก/.test(haystack)) return false;
  return /เงินเข้า|โอน|ผู้โอน|สลิป|invoice/i.test(haystack);
}

function isUnpaidInvoice(card: LiffFinancialWorkCard, statusTitle: string) {
  return (
    statusTitle === "ต้องระวัง" ||
    /invoice|ยังจ่ายไม่ได้|ยังไม่จ่าย/i.test(card.title)
  );
}

function isOutstandingReceivableCard(card: LiffFinancialWorkCard): boolean {
  if (
    card.objectRef.kind === "receivable" ||
    isReceivableBatchCard(card)
  ) {
    return true;
  }
  const workerId = card.availableActions.find((action) => action.dataNav?.workerId)
    ?.dataNav?.workerId;
  if (workerId === "bill-lin-receivable") return true;
  const hay = `${card.title} ${card.subtitle ?? ""}`;
  if (/ยอดค้าง|ชุดยอดค้าง/.test(hay)) return true;
  if (/\sค้าง\s+[\d,.]+\s*บาท/u.test(card.title ?? "")) return true;
  return false;
}

/** Test hook — queue row document glyph for a card. */
export function queueThumbGlyphForCard(
  card: LiffFinancialWorkCard,
): DocThumbGlyph {
  return queueThumbForCard(card).glyph;
}

function queueCardContextValue(card: LiffFinancialWorkCard, key: string): string {
  const row = card.contextRows?.find((entry) => entry.key === key);
  return String(row?.value ?? "").trim();
}

/** Expense queue thumb: withdraw.svg (ใบเสร็จ) vs invoice.svg (ใบแจ้งหนี้) when no photo. */
function expenseQueueThumbGlyph(card: LiffFinancialWorkCard): "withdraw" | "invoice" {
  const doc = queueCardContextValue(card, "document_type").toLowerCase();
  if (doc === "invoice" || doc === "ใบแจ้งหนี้") return "invoice";
  if (doc === "receipt" || doc === "ใบเสร็จ") return "withdraw";

  const hay = [
    card.title,
    card.subtitle ?? "",
    queueCardContextValue(card, "source"),
    queueCardContextValue(card, "source_extra"),
  ].join(" ");
  if (
    /ใบแจ้งหนี้จาก|Invoice จาก|ใบแจ้งหนี้|ใบกำกับ|\binvoice\b|ยังจ่ายไม่ได้|ยังไม่จ่าย/i.test(
      hay,
    )
  ) {
    return "invoice";
  }
  return "withdraw";
}

function queueThumbForCard(card: LiffFinancialWorkCard): {
  glyph: DocThumbGlyph;
  incoming: boolean;
  review: boolean;
} {
  const status = desktopStatus(card);
  const hay = `${card.title} ${card.subtitle ?? ""} ${card.objectRef.kind}`;
  const sourceHay = [
    hay,
    queueCardContextValue(card, "source"),
    queueCardContextValue(card, "source_extra"),
  ].join(" ");
  const workerId = card.availableActions.find((action) => action.dataNav?.workerId)
    ?.dataNav?.workerId;
  const review =
    status.title === "ต้องตรวจ" || card.surfaceState === "needs_review";
  const bank =
    /รายการธนาคาร|ยังไม่รู้ผู้โอน|ไม่ทราบผู้โอน|ไม่รู้ผู้โอน/.test(hay);
  const expense =
    workerId === "bill-lin-expense" ||
    card.objectRef.kind === "expense" ||
    /ใบเสร็จจาก|ใบแจ้งหนี้จาก|ใบเสร็จ|รายจ่าย|เงินออก|ตลาดสด|เงินสดหน้าร้าน/.test(
      sourceHay,
    );
  const invoice =
    isUnpaidInvoice(card, status.title) ||
    /ใบแจ้งหนี้จาก|invoice|ยังจ่ายไม่ได้/i.test(sourceHay);
  const outstanding =
    isOutstandingReceivableCard(card) || /ยอดค้างจาก|ยอดค้าง|ชุดยอดค้าง/.test(sourceHay);
  const slip =
    /สลิปจาก|สลิปมาจาก|\bสลิป\b/.test(sourceHay) && !expense && !outstanding;

  if (expense) {
    const glyph = isUnpaidInvoice(card, status.title)
      ? "invoice"
      : expenseQueueThumbGlyph(card);
    return { glyph, incoming: false, review };
  }
  if (invoice) return { glyph: "invoice", incoming: false, review };
  if (bank) return { glyph: "bank", incoming: true, review };
  if (outstanding) {
    return { glyph: "receipt", incoming: true, review };
  }
  if (slip) return { glyph: "deposit", incoming: true, review };
  return { glyph: "deposit", incoming: true, review };
}

function useQueueRowWorkspaceId(): string {
  const searchParams = useSearchParams();
  return (
    searchParams?.get("workspaceId") ||
    searchParams?.get("workspace_id") ||
    ""
  ).trim();
}

function useQueueRowOwnerUserId(): string {
  const searchParams = useSearchParams();
  return (
    searchParams?.get("owner_user_id") ||
    searchParams?.get("ownerUserId") ||
    getUserIdFromAuthToken() ||
    ""
  ).trim();
}

function WorkQueueThumb({
  card,
  workspaceId: workspaceIdProp,
}: {
  card: LiffFinancialWorkCard;
  workspaceId?: string | null;
}) {
  const workspaceFromUrl = useQueueRowWorkspaceId();
  const workspaceId = (workspaceIdProp || "").trim() || workspaceFromUrl;
  const ownerUserId = useQueueRowOwnerUserId();
  const thumb = queueThumbForCard(card);
  const imageUrl = resolveQueueCardImageUrl(card, workspaceId, ownerUserId);
  return (
    <FinancialWorkDocThumb
      {...thumb}
      imageUrl={imageUrl}
      testId="fw-queue-thumb"
    />
  );
}

function WorkQueueClosedThumb({
  card,
  workspaceId: workspaceIdProp,
}: {
  card: LiffFinancialWorkCard;
  workspaceId?: string | null;
}) {
  const workspaceFromUrl = useQueueRowWorkspaceId();
  const workspaceId = (workspaceIdProp || "").trim() || workspaceFromUrl;
  const ownerUserId = useQueueRowOwnerUserId();
  const thumb = queueThumbForCard(card);
  const imageUrl = resolveQueueCardImageUrl(card, workspaceId, ownerUserId);
  // Expenses without a photo use withdraw.svg / invoice.svg — not the history glyph.
  const expenseDocFallback =
    thumb.glyph === "withdraw" || thumb.glyph === "invoice";
  const glyph = expenseDocFallback
    ? thumb.glyph
    : imageUrl || thumb.incoming
      ? "history"
      : thumb.glyph;
  return (
    <FinancialWorkDocThumb
      glyph={glyph}
      incoming={thumb.incoming}
      review={false}
      imageUrl={imageUrl}
      testId="fw-queue-thumb"
    />
  );
}

function matchedOutMinor(card: LiffFinancialWorkCard): number | null {
  const evidenceMatched = (card.evidenceItems ?? [])
    .map((item) => item.matchedAmountMinor)
    .filter((value): value is number => value != null);
  if (evidenceMatched.length > 0) {
    return evidenceMatched.reduce((sum, value) => sum + value, 0);
  }
  if (card.requirementStatus?.complete && card.amountSummary) {
    return card.amountSummary.primaryAmountMinor;
  }
  return null;
}

function incomingPaidMinor(card: LiffFinancialWorkCard): number | null {
  const fromChild = (card.outstanding?.items ?? [])
    .map((row) => row.paidAmountMinor)
    .find((value): value is number => value != null && value > 0);
  if (fromChild != null) return fromChild;
  return null;
}

function describeOpenWorkRow(card: LiffFinancialWorkCard) {
  const status = desktopStatus(card);
  const occurredAt = cardOccurredAt(card);
  const incomeCard = isIncomeCard(card);
  const askKind = askKindFromCard(card);
  const paidMinor = incomingPaidMinor(card);
  const remainingMinor =
    card.outstanding?.remainingAmountMinor ??
    (card.amountSummary?.basis === "remaining"
      ? card.amountSummary.primaryAmountMinor
      : null);
  const retroGrossMinor = retroMatchGrossMinor(card);
  const partialMatch =
    askKind !== "retro_match" &&
    (paidMinor ?? 0) > 0 &&
    remainingMinor != null;
  const waitingMatch =
    !partialMatch &&
    (status.title === "รอจับคู่" ||
      status.title === "รอการตัดสินใจ" ||
      isSlipOwnerAskCard(card));
  const unpaid = isUnpaidInvoice(card, status.title);
  const related =
    retroGrossMinor != null
      ? bahtFromMinor(retroGrossMinor)
      : remainingMinor != null
        ? bahtFromMinor(remainingMinor)
        : card.amountSummary
          ? bahtFromMinor(card.amountSummary.primaryAmountMinor)
          : "—";
  let moneyIn =
    askKind === "retro_match"
      ? null
      : paidMinor != null && paidMinor > 0
        ? bahtFromMinor(paidMinor)
        : null;
  const relatedNote =
    askKind === "retro_match"
      ? retroGrossMinor != null
        ? "ยอดค้าง"
        : null
      : partialMatch && card.objectRef.kind === "receivable"
        ? "ยอดเหลือ"
      : waitingMatch || partialMatch
        ? "ยอดรอตรวจ"
        : moneyIn
          ? "ยอดเหลือ"
          : null;
  const outMinor = matchedOutMinor(card);
  let moneyOut = outMinor != null ? bahtFromMinor(outMinor) : null;
  // เงินเข้า and เงินออกจริง are mutually exclusive per row.
  if (incomeCard) {
    moneyOut = null;
  } else {
    moneyIn = null;
  }
  const source = card.contextRows?.find((row) => row.key === "source")?.value ?? "";
  const haystack = [
    displayWorkTitle(card.title),
    card.subtitle,
    source,
    isSharedBatchCard(card) ? batchSetLabel(cardBatchSize(card), cardBatchClosed(card)) : null,
    status.title,
    status.detail,
    related,
    moneyIn,
    moneyOut,
    card.objectRef.kind,
    occurredAt ? formatThaiDatetime(occurredAt) : null,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return {
    status,
    occurredAt,
    incomeCard,
    waitingMatch,
    unpaid,
    related,
    relatedNote,
    moneyIn,
    moneyOut,
    source,
    haystack,
    moneyStatus: unpaid
      ? "ยังไม่จ่าย"
      : waitingMatch && incomeCard
        ? "รอจับคู่"
        : null,
  };
}

export function buildWorkQueueRowModel(
  card: LiffFinancialWorkCard,
  args: {
    filter: QueueFilterId;
    grouped?: boolean;
    closedDisplay?: ClosedQueueRowDisplay;
  },
): WorkQueueRowModel {
  const closedView = queueRowShowsClosedStyle(args.filter, card);
  const grouped = Boolean(args.grouped);
  const open = describeOpenWorkRow(card);

  if (!closedView) {
    const rawSource = open.source?.trim() || "";
    const sourceChannel = rawSource
      ? closedProvenanceSourceLabel(card)
      : "";
    return {
      card,
      closedView: false,
      fromIncome: false,
      grouped,
      occurredAt: open.occurredAt,
      title: displayWorkTitle(card.title),
      subtitle: card.subtitle ?? null,
      matchLabel: null,
      sourceLabel:
        sourceChannel && sourceChannel !== card.subtitle ? sourceChannel : null,
      related: open.related,
      relatedNote: open.relatedNote,
      moneyIn: open.moneyIn,
      moneyOut: open.moneyOut,
      status: open.status,
      moneyStatus: open.moneyStatus,
      incomeCard: open.incomeCard,
      waitingMatch: open.waitingMatch,
      unpaid: open.unpaid,
      haystack: open.haystack,
    };
  }

  const closed = args.closedDisplay ?? describeClosedQueueRow(card);
  const closedByDetail = closed.closedByLabel
    ? `ยอด ${closed.closedByLabel}`
    : closed.statusDetail;
  return {
    card,
    closedView: true,
    fromIncome: closed.fromIncome,
    grouped,
    occurredAt: closed.occurredAt || cardOccurredAt(card) || open.occurredAt,
    title: closed.title,
    subtitle: null,
    matchLabel: closed.matchLabel,
    sourceLabel: closed.sourceLabel,
    related: closed.related,
    relatedNote: null,
    moneyIn: closed.moneyIn,
    moneyOut: closed.moneyOut,
    status: {
      title: closed.statusTitle,
      detail: closedByDetail,
      tone: "success",
    },
    moneyStatus: null,
    incomeCard: closed.fromIncome,
    waitingMatch: false,
    unpaid: false,
    haystack: open.haystack,
  };
}

export function workQueueRowHaystack(
  card: LiffFinancialWorkCard,
  filter: QueueFilterId,
): string {
  return buildWorkQueueRowModel(card, { filter }).haystack;
}

function rowSurfaceClass(model: WorkQueueRowModel): string {
  if (model.closedView) {
    return `border-b border-aera-lightgray hover:bg-aera-offwhite/70 ${
      model.grouped ? "bg-aera-warmcream/20 hover:bg-aera-warmcream/70" : ""
    }`;
  }
  return model.grouped
    ? "border-b border-aera-amber/20 bg-aera-warmcream/20 hover:bg-aera-warmcream/70"
    : "border-b border-aera-lightgray hover:bg-aera-offwhite/70";
}

const FORMATTED_QUEUE_DATETIME_RE = /^(.+?) \((\d{2}:\d{2})\)$/;

function queueRowDatetimeParts(
  value: string | null,
): { dateLabel: string; timeLabel: string } | null {
  if (!value || value === "—") return null;
  const text = value.trim();
  const formatted = text.match(FORMATTED_QUEUE_DATETIME_RE);
  if (formatted) {
    return { dateLabel: formatted[1], timeLabel: formatted[2] };
  }
  const date = parseDatetimeInput(value);
  if (!date) return null;
  return formatThaiDatetimePartsFromDate(date);
}

function QueueRowDate(props: { model: WorkQueueRowModel }) {
  // Always stack — inline "date (HH:MM)" overflows the narrow date col and
  // paints under the doc thumb (open/grouped receivable rows especially).
  const parts = queueRowDatetimeParts(props.model.occurredAt);
  if (!parts) {
    return <span className="text-aera-lightslate">—</span>;
  }
  return (
    <span className="inline-flex flex-col items-start gap-0 tabular-nums">
      <span
        className={
          props.model.closedView
            ? "font-semibold leading-5 text-aera-charcoal"
            : "font-medium leading-5 text-aera-gray"
        }
      >
        {parts.dateLabel}
      </span>
      <span className="text-xxs font-medium leading-5 text-aera-lightslate">
        {parts.timeLabel}
      </span>
    </span>
  );
}

function QueueRowTitleBlock(props: { model: WorkQueueRowModel }) {
  const { model } = props;
  return (
    <>
      <p className="line-clamp-2 break-words font-semibold text-aera-charcoal">
        {model.title}
      </p>
      {model.closedView && model.matchLabel ? (
        <p
          className="mt-0.5 truncate text-xs text-aera-gray"
          data-testid="fw-closed-match-label"
        >
          {model.matchLabel}
        </p>
      ) : null}
      {!model.closedView && model.subtitle ? (
        <p className="mt-0.5 truncate text-xs text-aera-gray">{model.subtitle}</p>
      ) : null}
      {model.sourceLabel ? (
        <p
          className="mt-0.5 truncate text-xs text-aera-lightslate"
          data-testid={model.closedView ? "fw-closed-source-label" : undefined}
        >
          {model.sourceLabel}
        </p>
      ) : null}
    </>
  );
}

function effectiveMoneyOut(model: WorkQueueRowModel): string | null {
  // เงินเข้า and เงินออกจริง are mutually exclusive.
  if (model.moneyIn && model.moneyOut) return null;
  return model.moneyOut;
}

function QueueRowMoneyInCell({ model }: { model: WorkQueueRowModel }) {
  const { moneyIn } = model;
  if (model.closedView) {
    return moneyIn ? (
      <span className="text-aera-green">{moneyIn}</span>
    ) : (
      <span className="text-aera-charcoal">—</span>
    );
  }
  if (moneyIn) {
    return <span className="text-aera-green">{moneyIn}</span>;
  }
  if (model.waitingMatch && model.incomeCard) {
    return (
      <span className="flex flex-col">
        <span className="text-aera-charcoal">—</span>
        <span className="text-xs font-medium text-aera-green">รอจับคู่</span>
      </span>
    );
  }
  if (model.incomeCard && model.card.amountSummary && !model.unpaid) {
    return <span className="text-aera-green">{model.related}</span>;
  }
  return <span className="text-aera-charcoal">—</span>;
}

function QueueRowMoneyOutCell({ model }: { model: WorkQueueRowModel }) {
  const moneyOut = effectiveMoneyOut(model);
  if (model.closedView) {
    return moneyOut ? (
      <span className="text-aera-red">{moneyOut}</span>
    ) : (
      <span className="text-aera-charcoal">—</span>
    );
  }
  if (moneyOut) {
    return <span className="text-aera-red">{moneyOut}</span>;
  }
  if (model.unpaid) {
    return (
      <span className="flex flex-col text-aera-charcoal">
        <span>—</span>
        <span className="text-xs font-medium text-aera-red">ยังไม่จ่าย</span>
      </span>
    );
  }
  if (!model.incomeCard && model.card.amountSummary) {
    return (
      <span className="font-medium text-aera-red">
        −{model.related.replace(/^฿/, "฿")}
      </span>
    );
  }
  return <span className="text-aera-charcoal">—</span>;
}

function QueueRowRelatedCell({ model }: { model: WorkQueueRowModel }) {
  return (
    <span className="flex flex-col">
      <span>{model.related}</span>
      {model.relatedNote ? (
        <span className="text-xs font-medium text-aera-gray">{model.relatedNote}</span>
      ) : null}
    </span>
  );
}

export function FinancialWorkQueueRow(props: {
  card: LiffFinancialWorkCard;
  filter: QueueFilterId;
  variant: "mobile" | "desktop";
  grouped?: boolean;
  closedDisplay?: ClosedQueueRowDisplay;
  workspaceId?: string | null;
  onOpen: (card: LiffFinancialWorkCard) => void;
}) {
  const model = buildWorkQueueRowModel(props.card, {
    filter: props.filter,
    grouped: props.grouped,
    closedDisplay: props.closedDisplay,
  });

  if (props.variant === "mobile") {
    return (
      <button
        type="button"
        data-testid="fw-work-card"
        data-object-id={getDisplayText(model.card.objectRef.id)}
        data-from-batch={model.grouped ? "true" : "false"}
        data-closed-view={model.closedView ? "true" : "false"}
        data-income-sourced={model.fromIncome ? "true" : "false"}
        onClick={() => props.onOpen(model.card)}
        className={`relative flex w-full cursor-pointer items-start gap-3 px-3 py-4 text-left last:border-b-0 ${rowSurfaceClass(model)}`}
      >
        {!model.closedView ? (
          <span className={workRowRailClass(model.status)} aria-hidden />
        ) : null}
        <div className="w-[6.75rem] shrink-0 pt-0.5">
          <QueueRowDate model={model} />
        </div>
        {model.closedView ? (
          <WorkQueueClosedThumb
            card={model.card}
            workspaceId={props.workspaceId}
          />
        ) : (
          <WorkQueueThumb card={model.card} workspaceId={props.workspaceId} />
        )}
        <div className="min-w-0 flex-1">
          {model.closedView ? (
            <>
              <QueueRowTitleBlock model={model} />
              <div className="mt-2 flex flex-wrap items-end justify-between gap-x-3 gap-y-1.5">
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs tabular-nums">
                  {model.moneyIn ? (
                    <span className="text-aera-green">{model.moneyIn}</span>
                  ) : null}
                  {effectiveMoneyOut(model) ? (
                    <span className="text-aera-charcoal">{effectiveMoneyOut(model)}</span>
                  ) : null}
                  <span className="font-semibold text-aera-amberdark">
                    {model.related}
                  </span>
                </div>
                <WorkQueueStatusMark {...model.status} />
              </div>
            </>
          ) : (
            <>
              <div className="flex items-start justify-between gap-2">
                <p className="min-w-0 font-semibold text-aera-charcoal">{model.title}</p>
                {model.moneyStatus ? (
                  <span
                    className={`shrink-0 text-xs font-medium ${
                      model.unpaid ? "text-aera-red" : "text-aera-green"
                    }`}
                  >
                    — {model.moneyStatus}
                  </span>
                ) : (
                  <span className="hidden shrink-0 font-semibold tabular-nums text-aera-charcoal min-[768px]:inline">
                    {model.moneyIn ?? model.related}
                  </span>
                )}
              </div>
              {model.subtitle ? (
                <p className="mt-0.5 line-clamp-2 text-xs text-aera-gray">
                  {model.subtitle}
                </p>
              ) : null}
              {model.sourceLabel ? (
                <p className="mt-0.5 text-xs text-aera-lightslate">{model.sourceLabel}</p>
              ) : null}
              <div className="mt-2 flex flex-col items-start gap-1.5">
                <span className="text-sm font-semibold tabular-nums text-aera-charcoal min-[768px]:hidden">
                  {model.related}
                </span>
                <WorkQueueStatusMark {...model.status} />
              </div>
            </>
          )}
        </div>
      </button>
    );
  }

  return (
    <tr
      data-testid="fw-work-card"
      data-object-id={getDisplayText(model.card.objectRef.id)}
      data-from-batch={model.grouped ? "true" : "false"}
      data-closed-view={model.closedView ? "true" : "false"}
      data-income-sourced={model.fromIncome ? "true" : "false"}
      className={`cursor-pointer last:border-b-0 ${rowSurfaceClass(model)}`}
      onClick={() => props.onOpen(model.card)}
    >
      <td className="relative min-w-[7rem] overflow-hidden px-3 py-3 align-middle">
        {!model.closedView ? (
          <span className={workRowRailClass(model.status)} aria-hidden />
        ) : null}
        <QueueRowDate model={model} />
      </td>
      <td className="px-3 py-3 align-top">
        <div className="flex min-w-0 items-start gap-3">
          <div className="shrink-0">
            {model.closedView ? (
              <WorkQueueClosedThumb
                card={model.card}
                workspaceId={props.workspaceId}
              />
            ) : (
              <WorkQueueThumb
                card={model.card}
                workspaceId={props.workspaceId}
              />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <QueueRowTitleBlock model={model} />
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-3 align-top tabular-nums">
        <QueueRowMoneyInCell model={model} />
      </td>
      <td className="whitespace-nowrap px-3 py-3 align-top tabular-nums">
        <QueueRowMoneyOutCell model={model} />
      </td>
      <td className="whitespace-nowrap px-3 py-3 align-top font-semibold tabular-nums text-aera-amberdark">
        <QueueRowRelatedCell model={model} />
      </td>
      <td className="px-3 py-3 align-top">
        <WorkQueueStatusMark {...model.status} />
      </td>
    </tr>
  );
}
